import { Link } from "react-router-dom";
import { ArrowLeft, Laptop2, EyeOff, Zap, Mic, Brain, Shield, Download, CheckCircle2 } from "lucide-react";

const INTEL_URL = "/downloads/Project-Wingman-1.0.0-intel.dmg";
const ARM_URL   = "/downloads/Project-Wingman-1.0.0-arm64.dmg";
const INSTALL_CMD = "curl -fsSL https://projectwingman.thesecretsociety.in/downloads/install.sh | bash";

const ARM_SHA256   = "43eab6c40b76b8ee09da87c104cd3694a92a9daccbbf433da100dcf63f057ec4";
const INTEL_SHA256 = "8d1cae6bb43eb19ecce7d23d08ad7c73093eabc8c83622937c7c25dce314faee";

const FEATURES = [
  {
    icon: Zap,
    title: "Real-time AI answers",
    body: "Every question is answered live as the conversation happens — no copy-paste, no tab-switching.",
  },
  {
    icon: Mic,
    title: "Live transcription",
    body: "Your microphone feed is transcribed automatically and sent to AI with one keypress.",
  },
  {
    icon: Brain,
    title: "Full interview suite",
    body: "Resume builder, LinkedIn optimizer, cover letters — all in one native Mac window.",
  },
  {
    icon: Shield,
    title: "Always-on-top overlay",
    body: "The overlay floats above every other app so the AI answer is always in your peripheral view.",
  },
];

const OVERLAY_BULLETS = [
  "Translucent panel (you see the answer; background remains ~50% visible)",
  "Floats above Zoom, Teams, Skype, Google Meet — even in full-screen",
  "Completely invisible to screen-share APIs — your interviewer sees nothing",
  "Adjustable opacity (20 – 95%) via the on-panel slider",
  "Pass-through mode — clicks fall through to the window underneath",
  "One-keystroke toggle: Cmd + Shift + W",
];

export default function DownloadMac() {
  return (
    <div className="min-h-screen bg-[#030303] text-[#f0f0f0]" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      {/* Nav */}
      <nav className="border-b border-white/[0.06] px-6 py-4 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 hover:text-white transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to home
        </Link>
        <span className="text-white/10 select-none">|</span>
        <span className="text-xs uppercase tracking-widest text-neutral-600">Mac App</span>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-20">

        {/* Hero */}
        <div className="mb-20">
          <div className="inline-flex items-center gap-2 border border-[#D3FF24]/30 bg-[#D3FF24]/5 px-3 py-1 rounded-sm mb-8">
            <Laptop2 className="w-3 h-3 text-[#D3FF24]" />
            <span className="text-[10px] uppercase tracking-widest text-[#D3FF24] font-semibold">Mac Desktop App</span>
          </div>

          <h1 className="text-4xl font-light tracking-tight mb-5 leading-tight">
            Wingman AI<br />
            <span className="text-[#D3FF24]">for Mac</span>
          </h1>

          <p className="text-neutral-400 text-base leading-relaxed max-w-xl mb-10">
            The full Wingman experience — live AI answers, resume tools, LinkedIn optimizer — wrapped in a native Mac app with an invisible overlay your interviewer will never see.
          </p>

          {/* Recommended: one-line Terminal install (no Gatekeeper warnings) */}
          <div className="border border-[#D3FF24]/30 bg-[#D3FF24]/5 rounded p-5 mb-8 max-w-2xl">
            <p className="text-[10px] uppercase tracking-widest text-[#D3FF24] font-semibold mb-2">
              Recommended — install in one step
            </p>
            <p className="text-sm text-neutral-400 mb-3">
              Paste this into Terminal. It detects your Mac's chip, downloads the right build, and installs it —
              with none of the "damaged app" security warnings a browser download triggers.
            </p>
            <code
              className="block text-[12px] text-[#f0f0f0] bg-black/60 border border-white/10 rounded px-3 py-2.5 break-all select-all cursor-pointer"
              onClick={(e) => { navigator.clipboard?.writeText(INSTALL_CMD); }}
              title="Click to copy"
            >
              {INSTALL_CMD}
            </code>
            <p className="text-[10px] text-neutral-600 mt-2 uppercase tracking-wider">Click the command to copy it</p>
          </div>

          {/* Alternative: direct zip downloads — Intel and Apple Silicon */}
          <p className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">Or download the zip manually</p>
          <div className="flex flex-wrap gap-4 mb-4">
            <a
              href={ARM_URL}
              className="group inline-flex items-center gap-3 bg-[#D3FF24] text-black px-7 py-4 text-xs uppercase tracking-widest font-bold hover:bg-white transition-colors"
            >
              <Download className="w-4 h-4" />
              Apple Silicon (M1 / M2 / M3)
            </a>
            <a
              href={INTEL_URL}
              className="group inline-flex items-center gap-3 border border-white/20 px-7 py-4 text-xs uppercase tracking-widest font-medium hover:bg-white/[0.04] hover:border-white/30 transition-colors"
            >
              <Download className="w-4 h-4 text-[#D3FF24]" />
              Intel Mac (x64)
            </a>
          </div>
          <p className="text-[11px] text-neutral-600 uppercase tracking-wider">
            macOS 12 Monterey or later · Disk image — open and drag to Applications
          </p>
        </div>

        {/* Install instructions */}
        <div className="border border-white/[0.07] bg-white/[0.02] rounded p-6 mb-20">
          <h3 className="text-xs uppercase tracking-widest text-[#D3FF24] mb-4 font-semibold">Manual install (only if you downloaded a .dmg)</h3>
          <ol className="space-y-3">
            {[
              "Download the disk image for your Mac (M-series = Apple Silicon, older Mac = Intel)",
              "Double-click the .dmg — a window opens showing Project Wingman",
              'Drag "Project Wingman.app" into your Applications folder',
              'Right-click the app in Applications → Open (first launch only, to confirm)',
              'If macOS still blocks it, open Terminal and run:  xattr -dr com.apple.quarantine "/Applications/Project Wingman.app"',
              'From then on, open normally from Launchpad or Spotlight',
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-neutral-400">
                <span className="text-[10px] font-bold text-[#D3FF24] bg-[#D3FF24]/10 border border-[#D3FF24]/20 rounded-sm px-1.5 py-0.5 shrink-0 mt-0.5">{i + 1}</span>
                {step}
              </li>
            ))}
          </ol>
          <p className="mt-5 text-[11px] text-neutral-600 leading-relaxed">
            The app isn't notarized by Apple yet, so macOS quarantines it after download and may say it's "damaged" or from an "unidentified developer." The one-time Terminal command in step 4 removes that quarantine flag and lets the app open normally — you only need to run it once.
          </p>

          <div className="mt-6 pt-5 border-t border-white/[0.07]">
            <p className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">Verify your download (SHA-256)</p>
            <div className="space-y-2 mb-2">
              <div>
                <span className="text-[10px] uppercase tracking-widest text-neutral-600">Apple Silicon</span>
                <code className="block text-[11px] text-neutral-400 bg-black/40 border border-white/10 rounded px-3 py-2 break-all mt-1">{ARM_SHA256}</code>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-widest text-neutral-600">Intel</span>
                <code className="block text-[11px] text-neutral-400 bg-black/40 border border-white/10 rounded px-3 py-2 break-all mt-1">{INTEL_SHA256}</code>
              </div>
            </div>
            <p className="text-[11px] text-neutral-600 leading-relaxed">
              In Terminal, run <code className="text-neutral-400">shasum -a 256 ~/Downloads/Project-Wingman-*.dmg</code> and confirm the hash matches before opening.
            </p>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/[0.06] mb-20" />

        {/* Invisible overlay feature */}
        <div className="mb-20">
          <div className="inline-flex items-center gap-2 border border-[#D3FF24]/20 px-3 py-1 rounded-sm mb-6">
            <EyeOff className="w-3 h-3 text-[#D3FF24]" />
            <span className="text-[10px] uppercase tracking-widest text-[#D3FF24] font-semibold">Exclusive — desktop only</span>
          </div>

          <h2 className="text-2xl font-light tracking-tight mb-4">
            Invisible screen-share overlay
          </h2>

          <p className="text-neutral-400 leading-relaxed max-w-2xl mb-8">
            When you join a Zoom, Teams, Skype, or Google Meet call and share your screen, the Wingman overlay is{" "}
            <span className="text-white font-medium">completely invisible to everyone on the call</span>.
            It sits on top of every other window — translucent, always visible to you — while the interviewer's screen
            capture sees absolutely nothing. On macOS this is powered by{" "}
            <code className="text-[#D3FF24] text-[11px] bg-white/5 px-1.5 py-0.5 rounded">CGWindowSetSharingType(kCGWindowSharingNone)</code>,
            the same private API used by macOS's own screenshot protection for FaceTime and Apple TV.
          </p>

          {/* Visual mockup */}
          <div className="relative rounded border border-white/10 bg-white/[0.02] p-6 mb-8 overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "repeating-linear-gradient(0deg,transparent,transparent 24px,rgba(255,255,255,0.03) 25px),repeating-linear-gradient(90deg,transparent,transparent 24px,rgba(255,255,255,0.03) 25px)" }} />

            <div className="mb-4">
              <p className="text-[10px] uppercase tracking-widest text-neutral-600 mb-2">Interviewer sees →</p>
              <div className="border border-white/10 rounded bg-black/40 p-4 text-sm text-neutral-500 italic">
                your screen — code editor, browser, nothing unusual
              </div>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-widest text-neutral-600 mb-2">You see →</p>
              <div className="relative border border-white/10 rounded bg-black/40 p-4 text-sm text-neutral-500 italic">
                your screen — code editor, browser, nothing unusual
                <div className="absolute top-2 right-2 w-56 border border-[#D3FF24]/30 rounded bg-black/60 backdrop-blur-sm p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D3FF24] opacity-80" />
                    <span className="text-[9px] uppercase tracking-widest text-[#D3FF24]">Wingman AI</span>
                  </div>
                  <p className="text-[10px] text-white/80 leading-relaxed">
                    Use the sliding window technique — iterate with two pointers at O(n) time complexity…
                  </p>
                </div>
              </div>
            </div>
          </div>

          <ul className="space-y-3">
            {OVERLAY_BULLETS.map((b) => (
              <li key={b} className="flex items-start gap-3 text-sm text-neutral-400">
                <CheckCircle2 className="w-4 h-4 text-[#D3FF24] shrink-0 mt-0.5" strokeWidth={1.5} />
                {b}
              </li>
            ))}
          </ul>
        </div>

        {/* Divider */}
        <div className="border-t border-white/[0.06] mb-20" />

        {/* Feature cards */}
        <div className="mb-20">
          <h2 className="text-2xl font-light tracking-tight mb-2">Everything in the web app, plus the overlay</h2>
          <p className="text-neutral-500 text-sm mb-10">All Wingman features are available natively — no browser required.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div key={title} className="border border-white/[0.07] bg-white/[0.02] p-6 rounded">
                <div className="w-8 h-8 border border-white/10 rounded flex items-center justify-center mb-4">
                  <Icon className="w-4 h-4 text-[#D3FF24]" strokeWidth={1.5} />
                </div>
                <h3 className="text-sm font-medium mb-1.5">{title}</h3>
                <p className="text-neutral-500 text-xs leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA footer */}
        <div className="border border-white/[0.07] bg-white/[0.02] rounded p-10 text-center">
          <h2 className="text-xl font-light tracking-tight mb-3">Ready to ace your next interview?</h2>
          <p className="text-neutral-500 text-sm mb-8 max-w-md mx-auto">
            Download the Mac app and get the invisible overlay — free with any Wingman plan.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href={ARM_URL} className="inline-flex items-center gap-2.5 bg-[#D3FF24] text-black px-7 py-3.5 text-xs uppercase tracking-widest font-bold hover:bg-white transition-colors">
              <Download className="w-4 h-4" />
              Apple Silicon
            </a>
            <a href={INTEL_URL} className="inline-flex items-center gap-2.5 border border-white/15 px-7 py-3.5 text-xs uppercase tracking-widest hover:bg-white/[0.04] hover:border-white/30 transition-all">
              <Download className="w-4 h-4 text-[#D3FF24]" />
              Intel Mac
            </a>
            <Link to="/pricing" className="inline-flex items-center gap-2 border border-white/15 px-7 py-3.5 text-xs uppercase tracking-widest hover:bg-white/[0.04] hover:border-white/30 transition-all">
              View pricing
            </Link>
          </div>
        </div>

      </main>

      <footer className="border-t border-white/[0.06] px-6 py-8 text-center text-[11px] text-neutral-700 uppercase tracking-widest">
        © {new Date().getFullYear()} Project Wingman · All rights reserved
      </footer>
    </div>
  );
}
